import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Icon, Input, Popup, SemanticICONS } from 'semantic-ui-react';
import TableComponent from '~/components/TableComponent';
import Layout from '~/components/Layout';
import i18n from '~/components/i18n';
import ClimaProgramQuery from '~/QueryHooks/ClimaProgramQuery';
import styled from 'styled-components';
import { useDebounce } from 'use-lodash-debounce';
import ExcelExport from '~/components/ExcelExport';
import FeedPurchaseViewModal from './Modals/FeedPurchaseViewModal';
import FeedPurchasePreviewModal, { FeedPurchaseFileSource } from './Modals/FeedPurchasePreviewModal';
import CompleteFeedPurchaseModal from './Modals/CompleteFeedPurchaseModal';
import DeletePurchaseModal from './Modals/DeletePurchaseModal';
import { formatDateGMT, formatNumber, handleErrors } from '~/utils/helpers';
import { RouteTypes } from '~/routes/constants';
import { ClimaProgramService } from '~/api/services/climaProgram';
import CustomIcon from '~/components/CustomIcon';
import FilterDropdown from './FilterDropdown';
import Filters from './Filters';
import FarmersList from './FarmersList';
import { El } from '~/components/Layout/style';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import CloseFeedPurchaseModal from './Modals/CloseFeedPurchaseModal';

const filterKeys = ['year', 'search', 'feedProducts', 'status', 'createdAtFrom', 'createdAtTo', 'purchaseDateFrom', 'purchaseDateTo', 'feedingFrom', 'feedingTo'] as const

const buildFilterQuery = (filters) => {
  const params = new URLSearchParams()
  filterKeys.forEach(key => {
    if (key === 'feedProducts') {
      if (filters[key]?.length) params.set(key, (filters[key] as string[]).join(','))
    } else if (filters[key]) {
      params.set(key, String(filters[key]))
    }
  })
  const query = params.toString()
  return query ? `?${query}` : ''
}

const FiltersWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 10px;
`;

const getTableColumns = () => [
  {
    name: i18n.t('Creation date'),
    key: 'createdAt',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'asc'
  },
  {
    name: i18n.t('EVS Nr.'),
    key: 'evsNumber',
    sorted: 'asc'
  },
  {
    name: i18n.t('Company name'),
    key: 'companyName',
    sorted: 'asc'
  },
  {
    name: i18n.t('Purchase date'),
    key: 'purchaseDate',
    type: 'date',
    format: 'DD.MM.YYYY',
    sorted: 'asc'
  },
  {
    name: i18n.t('Feeding from'),
    key: 'feedingFrom',
    type: 'date',
    format: 'DD.MM.YYYY',
    sorted: 'asc'
  },
  {
    name: i18n.t('Feeding to'),
    key: 'feedingTo',
    type: 'date',
    format: 'DD.MM.YYYY',
    sorted: 'asc'
  },
  {
    name: i18n.t('Feed Products'),
    key: 'articleDisplay'
  },
  {
    name: i18n.t('Agoline amount (in kg)'),
    key: 'agolineAmount',
    type: 'no-unit-number',
    sorted: 'asc'
  },
  {
    name: i18n.t('Purchased feed amount'),
    key: 'purchasedFeedAmount',
    type: 'no-unit-number',
    sorted: 'asc'
  },
  {
    name: i18n.t('Amount of minerals fed [calculated] (in kg)'),
    key: 'sumAnimalGroupsFeedAmount',
    type: 'no-unit-number',
    sorted: 'asc'
  },
  {
    name: i18n.t('Agoline Content'),
    key: 'agolineContent',
    type: 'no-unit-number',
    sorted: 'asc'
  },
  {
    name: i18n.t('Average - Number of lactating cows fed with Agolin'),
    key: 'dairyCowsNumber',
    sorted: 'asc'
  },
  {
    name: i18n.t('This corresponds to g Agolin per lactating cow per day'),
    type: 'no-unit-number',
    key: 'dairyCowsDailyAgolineAmountPerCow',
    sorted: 'asc'
  },
  {
    name: i18n.t('Average - Number of dry cows fed with Agolin'),
    key: 'dryCowsNumber',
    sorted: 'asc'
  },
  {
    name: i18n.t('This corresponds to g Agolin per dry cow per day'),
    type: 'no-unit-number',
    key: 'dryCowsDailyAgolineAmountPerCow',
    sorted: 'asc'
  },
  {
    name: i18n.t('Status'),
    key: 'statusLabel'
  },
  {
    name: i18n.t('Actions')
  }
];

const FeedPurchases: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const [purchaseId, setPurchaseId] = useState(null);
  const [completePurchaseId, setCompletePurchaseId] = useState<number | null>(null);
  const [previewFileSource, setPreviewFileSource] = useState<FeedPurchaseFileSource | null>(null);
  const [deletePurchaseId, setDeletePurchaseId] = useState(null)
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getTableColumns())
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [checkedFilters, setCheckedFilters] = useState<any[]>(() => {
    const stored = localStorage.getItem('feedPurchasesFilters');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        return [];
      }
    }
    return [];
  });
  const [selectedFarmerId, setSelectedFarmerId] = useState<string | null>(null);
  const [selectedFarmerRegistered, setSelectedFarmerRegistered] = useState<boolean>(false)
  const [farmerName, setFarmerName] = useState('')
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    year: Number(searchParams.get('year')) || currentYear,
    search: searchParams.get('search') || '',
    feedProducts: searchParams.get('feedProducts') ? searchParams.get('feedProducts').split(',') : [],
    createdAtFrom: searchParams.get('createdAtFrom') || '',
    createdAtTo: searchParams.get('createdAtTo') || '',
    purchaseDateFrom: searchParams.get('purchaseDateFrom') || '',
    purchaseDateTo: searchParams.get('purchaseDateTo') || '',
    feedingFrom: searchParams.get('feedingFrom') || '',
    feedingTo: searchParams.get('feedingTo') || '',
    status: searchParams.get('status') || '',
    farmerId: ''
  });
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [closeFeedPurchaseId, setCloseFeedPurchaseId] = useState(null)
  const debouncedSearchValue = useDebounce(search, 300);
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })

  const {
    data: purchasesData,
    isLoading: isBusy,
    isFetching: isFetchingPurchases,
    refetch: fetchData
  } = ClimaProgramQuery.getPurchases({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
    createdAtFrom: formatDateGMT(dataFiltered.createdAtFrom),
    createdAtTo: formatDateGMT(dataFiltered.createdAtTo),
    purchaseDateFrom: formatDateGMT(dataFiltered.purchaseDateFrom),
    purchaseDateTo: formatDateGMT(dataFiltered.purchaseDateTo),
    feedingFrom: formatDateGMT(dataFiltered.feedingFrom),
    feedingTo: formatDateGMT(dataFiltered.feedingTo)
  });

  const {
    data: indexData,
    isLoading: isLoadingOptions,
    refetch: fetchOptions
  } = ClimaProgramQuery.getPurchasesIndexData();

  useEffect(() => {
    fetchOptions()
    setTableColumnHeaders(getTableColumns())
  }, [t])

  useEffect(() => {
    fetchData();
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  useEffect(() => {
    const params = new URLSearchParams()
    filterKeys.forEach(key => {
      if (key === 'feedProducts') {
        if (dataFiltered[key]?.length) params.set(key, dataFiltered[key].join(','))
      } else if (dataFiltered[key]) {
        params.set(key, String(dataFiltered[key]))
      }
    })
    setSearchParams(params, { replace: true })
  }, [dataFiltered])

  const { filtered, rows: rawRows } = purchasesData ? purchasesData : { filtered: 0, rows: [] };

  const rows = rawRows?.map(row => ({
    ...row,
    articleDisplay: row.feedProductName || t('Customer mix'),
    cowsDisplay: `${formatNumber(row.dairyCowsNumber, 0) ?? '-'} / ${formatNumber(row?.dairyCowsNumber, 0) ?? '-'}`,
    isPdf: row.purchaseReceiptFilename?.toLowerCase()?.endsWith('.pdf') ? 1 : 0
  })) || [];

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const year = currentYear - i;
    return { key: year, value: year, text: year.toString() };
  });

  const articleOptions = [
    ...(indexData?.products?.map(article => ({
      key: article.id,
      value: article.id,
      text: article.name
    })) || [])
  ];

  const actions = [
    {
      popupMessage: t('Purchase details'),
      type: 'icon',
      name: 'eye',
      size: 'large',
      actionType: 'view'
    },
    {
      popupMessage: t('Edit'),
      type: 'link',
      name: 'pencil',
      size: 'large',
      actionType: 'edit',
      renderActions: getActions,
      link: RouteTypes.CLIMA_PROGRAM_FEED_PURCHASES_EDIT
    },
    {
      popupMessage: t('Preview'),
      type: 'icon',
      name: 'file pdf',
      size: 'large',
      actionType: 'preview',
      renderActions: getActions
    },
    {
      popupMessage: t('Download'),
      type: 'icon',
      name: 'cloud download',
      size: 'large',
      actionType: 'download'
    },
    {
      popupMessage: t('Complete the food purchase'),
      type: 'icon',
      name: 'check circle',
      size: 'large',
      actionType: 'complete',
      renderActions: getActions
    },
    {
      popupMessage: t('Delete food purchase'),
      type: 'icon',
      name: 'trash',
      size: 'large',
      actionType: 'delete',
      renderActions: getActions
    },
    {
      popupMessage: t('Completed'),
      type: 'icon',
      color: 'green',
      name: 'check circle',
      size: 'large',
      actionType: 'completed',
      renderActions: getActions
    }
  ];

  function getActions(row, actionType) {
    const action = actions.find(action => action.actionType === actionType)
    const isFileTypePdf = row?.purchaseReceiptFilename?.includes('.pdf')
    const farmerName = encodeURIComponent(`${row.companyName} ${row?.evsNumber ? `(${row?.evsNumber})` : ''}`)
    const isFeedCompleted = row?.status === 3
    switch (actionType) {
      case 'preview':
        return isFileTypePdf && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => handlePreview(row)}
            size='large'
          />}
        />
      case 'complete':
        return row?.status === 0 && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => setCompletePurchaseId(row?.id)}
            size='large'
          />}
        />
      case 'delete':
        return row?.status != 3 && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => setDeletePurchaseId(row?.id)}
            size='large'
          />}
        />
      case 'edit':
        return <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => navigate(`${action.link.replace(':id', row?.id)}${buildFilterQuery(dataFiltered)}#farmerName=${farmerName}`)}
            size='large'
          />}
        />
      case 'completed':
        return !isFeedCompleted && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => setCloseFeedPurchaseId(row?.id)}
            size='large'
          />}
        />
      default:
        return
    }
  }

  async function handleDownload(row) {
    setLoadingAction({
      type: 'download',
      id: row.id,
      value: true,
      idKey: 'id'
    })
    try {
      const response = await ClimaProgramService.getFile(row?.id)
      const link = document.createElement('a')
      link.href = response.data
      link.download = row?.purchaseReceiptOriginalFilename || ''
      link.click()
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  async function handlePreview(row) {
    setLoadingAction({
      type: 'preview',
      id: row.id,
      value: true,
      idKey: 'id'
    })
    setIsLoadingPreview(true)
    try {
      const response = await ClimaProgramService.getFile(row?.id)
      setPreviewFileSource({
        src: response.data,
        name: row?.purchaseReceiptOriginalFilename || row?.purchaseReceiptFilename || ''
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsLoadingPreview(false)
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'view':
        return setPurchaseId(row?.id);
      case 'download':
        return handleDownload(row)
      default:
        return;
    }
  }

  function handleCheckFilter(checked: boolean, filter: any) {
    if (checked) {
      const newChecked = [...checkedFilters, filter];
      setCheckedFilters(newChecked);
      localStorage.setItem('feedPurchasesFilters', JSON.stringify(newChecked));
    } else {
      const newChecked = checkedFilters.filter(f => f.key !== filter.key);
      setCheckedFilters(newChecked);
      setDataFiltered({ ...dataFiltered, [filter.fromKey]: '', [filter.toKey]: '', page: 0 });
      localStorage.setItem('feedPurchasesFilters', JSON.stringify(newChecked));
    }
  }

  function renderFilters() {
    return (
      <FiltersWrapper>
        <Dropdown
          selection
          selectOnBlur={false}
          value={dataFiltered.year}
          options={yearOptions}
          onChange={(_, data) => setDataFiltered({ ...dataFiltered, year: data.value as number, page: 0 })}
        />
        <Input
          icon={{ name: 'search', circular: true, link: true }}
          placeholder={t('EVS No. / Company Name')}
          value={search}
          onChange={(e, data) => setSearch(data.value)}
        />
        <Dropdown
          selection
          selectOnBlur={false}
          search
          placeholder={t('Filter by article')}
          clearable
          multiple
          value={dataFiltered.feedProducts}
          options={articleOptions}
          onChange={(_, data) => setDataFiltered({ ...dataFiltered, feedProducts: data.value as string[], page: 0 })}
        />
        {checkedFilters.length > 0 && (
          <Filters
            checkedFilters={checkedFilters}
            dataFiltered={dataFiltered}
            onChangeDataFiltered={(newData) => setDataFiltered(newData)}
          />
        )}
        <Dropdown
          selection
          selectOnBlur={false}
          placeholder={t('Status')}
          clearable
          value={dataFiltered.status}
          options={indexData?.statuses?.map(status => ({ value: status.key, text: status.label }))}
          onChange={(_, data) => setDataFiltered({ ...dataFiltered, status: data.value as string, page: 0 })}
        />
      </FiltersWrapper>
    );
  }

  function renderActions() {
    return (
      <>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          <FarmersList
            value={selectedFarmerId}
            onChange={(value, climateProgramRegistration, farmerName) => {
              setSelectedFarmerId(value);
              setSelectedFarmerRegistered(climateProgramRegistration)
              setFarmerName(farmerName)
            }}
            clearable
          />
          {selectedFarmerId && !selectedFarmerRegistered && <Popup
            content={t('Farmer has no registration')}
            trigger={<Icon name='warning sign' color='yellow' style={{ fontSize: '1.2em' }} />}
          />}
          {selectedFarmerId && selectedFarmerRegistered && <El.PageHeaderActionButton
            disabled={!selectedFarmerId}
            onClick={() => navigate(`${RouteTypes.CLIMA_PROGRAM_FEED_PURCHASES_CREATE.replace(':idFarmer', selectedFarmerId)}${buildFilterQuery(dataFiltered)}#farmerName=${encodeURIComponent(farmerName)}`)}
          >
            <Icon name='plus' />
            {t('Add purchase')}
          </El.PageHeaderActionButton>}
        </div>
        <FilterDropdown
          checkedFilters={checkedFilters}
          handleCheckFilter={handleCheckFilter}
        />
        <ExcelExport
          url={'climate-program/purchases/export'}
          fileName=''
          icon={'file excel'}
          downloadWithFileName
          data={{
            year: dataFiltered?.year,
            feedProducts: dataFiltered?.feedProducts
          }}
        />
      </>
    );
  }

  function renderPurchaseViewModal() {
    return (
      <FeedPurchaseViewModal
        purchaseId={purchaseId}
        onClose={() => setPurchaseId(null)}
      />
    );
  }

  function renderTable() {
    return (
      <TableComponent
        tableColumnHeaders={tableColumnHeaders}
        dataFiltered={dataFiltered}
        loadingAction={loadingAction}
        rows={rows || []}
        total={filtered || 0}
        handleChangeDataFiltered={(newDataFiltered, tableColumnHeaders) => {
          setDataFiltered({ ...dataFiltered, ...newDataFiltered })
          setTableColumnHeaders(tableColumnHeaders)
        }}
        handleClickAction={handleActionClick}
        animated
        isFetchingData={isFetchingPurchases}
        actions={actions}
      />
    );
  }

  function renderDeleteModal() {
    return <DeletePurchaseModal
      purchaseId={deletePurchaseId}
      onClose={() => setDeletePurchaseId(null)}
      onSuccess={fetchData}
    />
  }

  function renderCloseFeedPurchase() {
    return <CloseFeedPurchaseModal
      id={closeFeedPurchaseId}
      onClose={() => setCloseFeedPurchaseId(null)}
      onSuccess={fetchData}
    />
  }

  return (
    <Layout
      title={t('Feed Purchases')}
      segmented
      isBusy={isBusy || isLoadingOptions}
      filters={renderFilters()}
      actions={renderActions()}
    >
      {renderPurchaseViewModal()}
      <CompleteFeedPurchaseModal
        purchaseId={completePurchaseId}
        onClose={() => setCompletePurchaseId(null)}
        onSuccess={fetchData}
      />
      <FeedPurchasePreviewModal
        fileSource={previewFileSource}
        handleClose={() => setPreviewFileSource(null)}
        isBusy={isLoadingPreview}
      />
      {renderTable()}
      {renderDeleteModal()}
      {renderCloseFeedPurchase()}
    </Layout>
  );
};

export default FeedPurchases;
